import { Injectable } from '@angular/core';
import { MatDateFormats, NativeDateAdapter } from '@angular/material/core';

/**
 * Marqueur passé en `displayFormat` / `parseFormat` : il n'est jamais transmis à Intl,
 * il sert uniquement à reconnaître le champ de saisie dans l'adapter ci-dessous.
 */
const EUROPEAN_DATE_INPUT = 'dd/MM/yyyy';

const DATE_INPUT_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

/**
 * Le champ de saisie est figé en JJ/MM/AAAA, mais les libellés du calendrier restent
 * décrits par des options Intl : ce sont eux qui suivent la langue de l'utilisateur.
 */
export const EUROPEAN_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: EUROPEAN_DATE_INPUT
  },
  display: {
    dateInput: EUROPEAN_DATE_INPUT,
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
};

/**
 * NativeDateAdapter délègue l'ordre des champs à Intl, donc à la locale : en anglais le
 * champ basculerait en MM/JJ/AAAA. On force JJ/MM/AAAA en saisie comme en affichage et on
 * laisse tout le reste (noms de mois, de jours, premier jour de la semaine) suivre la locale.
 */
@Injectable()
export class EuropeanDateAdapter extends NativeDateAdapter {

  override format(date: Date, displayFormat: Object): string {
    // Une date invalide part vers l'implémentation native, qui lève l'erreur attendue par Material.
    if (displayFormat !== EUROPEAN_DATE_INPUT || !this.isValid(date)) {
      return super.format(date, displayFormat);
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');

    return `${day}/${month}/${date.getFullYear()}`;
  }

  override parse(value: unknown, parseFormat?: unknown): Date | null {
    const parts = typeof value === 'string' ? DATE_INPUT_PATTERN.exec(value.trim()) : null;

    // Date.parse, utilisé par l'implémentation native, lit "24/08/2026" comme une date
    // invalide : sans ce découpage explicite la saisie clavier serait impossible.
    if (!parts) {
      return super.parse(value, parseFormat);
    }

    const year = Number(parts[3]);
    const month = Number(parts[2]) - 1;
    const day = Number(parts[1]);

    const parsed = new Date(year, month, day);
    parsed.setFullYear(year);

    // Un 31/02 déborde silencieusement sur mars : on le rejette comme une saisie invalide.
    return parsed.getMonth() === month && parsed.getDate() === day ? parsed : new Date(NaN);
  }
}
